import { useWebClientApi } from "src/api";
import {
  fromRaces,
  toRacesUpdateReq,
  type RaceFormData,
} from "src/api/adapters";
import { useResponseRedirect } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import Disclaimer from "./EthnicityDisclaimer";
import type { DtoGetDemographicsResp } from "@porchpass/api-client";
import { isRaceOtherSectionValid, hasRace, test, validate } from "src/utils";
import {
  EcoaRaceFields,
  FormTable,
  FormTableBody,
  getRaceOtherTextFieldErrors,
  UserIdVerificationIcon,
} from "@porchpass/ui";
import { useMemo } from "react";
import {
  fromEcoaRaceFormData,
  toEcoaRaceFormData,
} from "src/forms/adapters/ecoa";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";

const validationMapper = () => ({
  races: [
    test(hasRace, "Must select at least one option"),
    test(isRaceOtherSectionValid, "Please enter the highlighted fields."),
  ],
});

export default () => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<RaceFormData>(
    {},
    () => api.getDemographics(),
    (data) => fromRaces(data as DtoGetDemographicsResp),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: RaceFormData) =>
      api.updateDemographics(toRacesUpdateReq(data)),
    );
  useResponseRedirect("/ethnicity/sex", response);

  const formErrors = validate(formData as never, validationMapper as never) as {
    races?: { message: string };
  };

  const raceValue = useMemo(
    () =>
      toEcoaRaceFormData(formData as Parameters<typeof toEcoaRaceFormData>[0]),
    [formData],
  );

  const raceOtherTextErrors = hasAttemptedFormSubmission
    ? getRaceOtherTextFieldErrors(raceValue.races)
    : {};
  const hasRaceOtherTextErrors = Object.keys(raceOtherTextErrors).length > 0;

  const visibleErrors = {
    ...fieldErrorsAfterSubmit(
      {
        races: hasRaceOtherTextErrors ? undefined : formErrors.races?.message,
      },
      hasAttemptedFormSubmission,
    ),
    ...raceOtherTextErrors,
  };

  return (
    <FormLayout
      title="Describe your race:"
      headerTitle="Ethnic Background"
      stepIndex={9}
      footerTitle="Next"
      clickHandler={() => void submitHandler(formData, formErrors as never)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Equal Credit Opportunity Act"
          headerIcon={
            <UserIdVerificationIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#CA7900]"
            />
          }
        >
          <FormTableBody>
            <EcoaRaceFields
              value={raceValue}
              onChange={(patch) => {
                const payload = fromEcoaRaceFormData({
                  ...raceValue,
                  ...patch,
                });
                updateField("races")(payload.races ?? []);
              }}
              errors={visibleErrors}
            />
          </FormTableBody>
        </FormTable>
        <Disclaimer />
      </Col>
    </FormLayout>
  );
};
